import React, { useState, startTransition } from 'react';
import {
  AppBar,
  Menu,
  MenuItem,
  Box,
  Container,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppState } from 'src/state-management/state-provider';
import clientService from 'src/clientService';
import { AccountCircle, Logout as LogoutIcon, Home, HelpOutline } from '@mui/icons-material';
import { profileNavs } from './nav.config';
import { UserRole } from 'src/api/Api';
import Logo from 'src/resources/images/logo-black 1.png';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  const { state, dispatch } = useAppState();

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleLogoClick = () => {
    const projectId = state.selectedProject?.projectDetails?.project_id;
    if (projectId) {
      startTransition(() => {
        navigate(`/project/${projectId}/overview`);
      });
    } else {
      startTransition(() => {
        navigate('/landing');
      });
    }
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    try {
      await clientService.request({
        path: '/api/v1/auth/logout',
        method: 'POST',
        secure: true,
      });
    } catch (error) {
      console.error('Logout API call failed:', error);
    }
    startTransition(() => {
      dispatch({ type: 'LOGOUT' });
      navigate('/auth');
    });
  };

  const isAdmin = state.user?.role.includes(UserRole.Admin);
  const isUserOrEmployee = state.user?.role.includes(UserRole.User) || state.user?.role.includes(UserRole.Employee);

  return (
    <AppBar color="default" sx={{ boxShadow: 1 }} position="fixed" className="header">
      <Container
        maxWidth={false}
        sx={{ maxWidth: '1736px' }}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingLeft: '1.75rem',
          paddingRight: '1.75rem',
          paddingTop: '0',
          paddingBottom: '0',
        }}
      >
        {/* Left: Logo */}
        <Box display="flex" alignItems="center" sx={{ minHeight: "72px" }}>
          <Box className="logo" display="flex" alignItems="center" height="100%" onClick={handleLogoClick}>
            <img src={Logo} alt="Logo" style={{ scale: 0.9 }} />
          </Box>
        </Box>

        <Box display="flex" alignItems="center" sx={{ minHeight: "73px" }}>
          {isUserOrEmployee && (
            <>
              <Tooltip title="Go to Home / Project Selection">
                <Button color="inherit" startIcon={<Home />} onClick={() => navigate('/landing')}>
                  Home
                </Button>
              </Tooltip>
              <Tooltip title="Help & Support">
                <Button color="inherit" startIcon={<HelpOutline />} onClick={() => navigate('/help-support')} sx={{ ml: 1 }}>
                  Help & Support
                </Button>
              </Tooltip>
            </>
          )}
          <IconButton
            onClick={handleUserMenuOpen}
            color="inherit"
            sx={{ ml: 1 }}
          >
            <AccountCircle fontSize="medium" />
          </IconButton>
          <Menu
            anchorEl={userMenuAnchorEl}
            open={Boolean(userMenuAnchorEl)}
            onClose={handleUserMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem disabled sx={{ fontWeight: 'bold', '&.Mui-disabled': { opacity: 1, color: 'text.primary' } }}>
              Hi, {state.user?.first_name}
            </MenuItem>
            <Divider />
            {profileNavs.map((nav) => {
              const Icon = nav.icon;
              const path = isAdmin ? `/admin${nav.path}` : nav.path;
              return (
                <MenuItem key={nav.text} onClick={() => {
                  startTransition(() => {
                    navigate(path);
                  });
                  handleUserMenuClose();
                }}>
                  <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
                  <ListItemText primary={nav.text} />
                </MenuItem>
              );
            })}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItem>
          </Menu>
        </Box>
      </Container>
    </AppBar>
  );
};

export default Header;
